import React from 'react';
import { Building2, CheckCircle, XCircle, Ban, Download, ChevronDown, ChevronUp, Edit, Trash2 } from 'lucide-react';

const companyStatusConfig = {
  en_attente: { label: 'En attente', color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/30', icon: () => <span /> },
  valide: { label: 'Validé', color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/30', icon: CheckCircle },
  refuse: { label: 'Refusé', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30', icon: XCircle },
  suspendu: { label: 'Suspendu', color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/30', icon: Ban },
};

export const CompaniesTab = ({
  companies, statusFilter, setStatusFilter, expandedId, setExpandedId,
  updateCompanyStatus, openCompanyEditModal, deleteCompany, search, t, API_URL
}) => {
  const companyCounts = { all: companies.length };
  companies.forEach(c => { companyCounts[c.status] = (companyCounts[c.status] || 0) + 1; });

  const filtered = companies.filter(c =>
    !search || c.company_name?.toLowerCase().includes(search.toLowerCase()) || c.siret?.includes(search) || c.email?.includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="b2b-companies-tab">
      <div className="flex gap-2 flex-wrap">
        {[
          { key: '', label: `Toutes (${companyCounts.all})` },
          { key: 'en_attente', label: `En attente (${companyCounts.en_attente || 0})` },
          { key: 'valide', label: `Validées (${companyCounts.valide || 0})` },
          { key: 'refuse', label: `Refusées (${companyCounts.refuse || 0})` },
          { key: 'suspendu', label: `Suspendues (${companyCounts.suspendu || 0})` },
        ].map(tab => (
          <button key={tab.key} onClick={() => setStatusFilter(tab.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${statusFilter === tab.key ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50' : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'}`}
            data-testid={`b2b-filter-${tab.key || 'all'}`}>
            {tab.label}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="text-center py-10 text-gray-400">{t('adminB2b.noCompany')}</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(company => {
            const config = companyStatusConfig[company.status] || companyStatusConfig.en_attente;
            const StatusIcon = config.icon;
            const expanded = expandedId === company.id;
            return (
              <div key={company.id} className={`border rounded-xl overflow-hidden transition-colors ${config.bg}`} data-testid={`b2b-company-${company.id}`}>
                <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedId(expanded ? null : company.id)}>
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <Building2 className={`w-5 h-5 ${config.color} flex-shrink-0`} />
                    <div className="min-w-0">
                      <p className="text-white font-bold truncate">{company.company_name}</p>
                      <p className="text-sm text-gray-400">{company.manager_name} - {company.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${config.bg} ${config.color} border`}>
                      <StatusIcon className="w-3 h-3 inline mr-1" />{config.label}
                    </span>
                    {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </div>
                {expanded && (
                  <div className="px-4 pb-4 border-t border-white/5 pt-4 space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div><span className="text-gray-500">Téléphone:</span> <span className="text-white ml-1">{company.phone}</span></div>
                      <div><span className="text-gray-500">SIRET:</span> <span className="text-white ml-1 font-mono">{company.siret}</span></div>
                      <div><span className="text-gray-500">Adresse:</span> <span className="text-white ml-1">{company.address}</span></div>
                      <div><span className="text-gray-500">Inscrit le:</span> <span className="text-white ml-1">{new Date(company.created_at).toLocaleDateString('fr-FR')}</span></div>
                      {company.validated_by && <div><span className="text-gray-500">Validé par:</span> <span className="text-white ml-1">{company.validated_by}</span></div>}
                    </div>
                    {company.kbis_file && (
                      <a href={`${API_URL}/api/admin/b2b/kbis/${company.kbis_file}`} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm text-cyan-400 transition-colors"
                        data-testid={`b2b-kbis-${company.id}`}>
                        <Download className="w-4 h-4" /> Télécharger KBIS
                      </a>
                    )}
                    <div className="flex gap-2 flex-wrap pt-2">
                      {company.status !== 'valide' && <button onClick={() => updateCompanyStatus(company.id, 'valide')} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-colors flex items-center gap-1" data-testid={`b2b-validate-${company.id}`}><CheckCircle className="w-4 h-4" /> Valider</button>}
                      {company.status !== 'refuse' && <button onClick={() => updateCompanyStatus(company.id, 'refuse')} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-colors flex items-center gap-1" data-testid={`b2b-refuse-${company.id}`}><XCircle className="w-4 h-4" /> Refuser</button>}
                      {company.status === 'valide' && <button onClick={() => updateCompanyStatus(company.id, 'suspendu')} className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-semibold transition-colors flex items-center gap-1" data-testid={`b2b-suspend-${company.id}`}><Ban className="w-4 h-4" /> Suspendre</button>}
                      {company.status === 'suspendu' && <button onClick={() => updateCompanyStatus(company.id, 'valide')} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-colors flex items-center gap-1" data-testid={`b2b-reactivate-${company.id}`}><CheckCircle className="w-4 h-4" /> Reactiver</button>}
                      <button onClick={() => openCompanyEditModal(company)} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-semibold transition-colors flex items-center gap-1" data-testid={`b2b-edit-${company.id}`}><Edit className="w-4 h-4" /> Modifier</button>
                      <button onClick={() => deleteCompany(company)} className="px-4 py-2 bg-red-900 hover:bg-red-800 text-red-300 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1" data-testid={`b2b-delete-${company.id}`}><Trash2 className="w-4 h-4" /> Supprimer</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
